/**
 * BillingBanner — top-of-shell notice that surfaces when the family's
 * subscription needs attention (trial ending, card declined, subscription
 * lapsed).
 *
 * Mirrors the poppy accent-stripe treatment of DescriptionCard's `attention`
 * variant so the visual grammar of "you should look at this" stays
 * consistent across the app. Renders nothing at all when things are fine,
 * so it costs no vertical space on the happy path.
 *
 * Refresh cadence: fetches on mount, then every 5 minutes. The parent PWA
 * runs largely idle — polling more often just burns backend cost and the
 * user's bandwidth for a signal that changes on human timescales.
 */

import { useEffect, useState } from 'preact/hooks';
import { authSession } from '../state/session';
import {
  getBillingStatus,
  BillingNotAvailableError,
  type BillingStatus,
} from '../data/billing';
import { Button } from './Button';
import { startCheckout, openPortal } from '../data/billing';
import { useVisiblePoll } from '../hooks/useVisiblePoll';

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

interface Notice {
  copy: string;
  actionLabel: string;
  action: 'checkout' | 'portal';
}

/**
 * Derive the banner copy + action from a status snapshot, or `null` when
 * nothing needs saying. Order matters here: past_due precedes trial-expiry
 * fallbacks so a lapsed card doesn't get masked by trial messaging.
 */
function noticeFor(s: BillingStatus, now: number): Notice | null {
  if (s.subscription_status === 'active') return null;

  if (s.subscription_status === 'past_due') {
    return {
      copy: 'Your card was declined. Update payment to keep capture running.',
      actionLabel: 'Update card',
      action: 'portal',
    };
  }

  if (
    s.subscription_status === 'canceled' ||
    s.subscription_status === 'unpaid'
  ) {
    return {
      copy: 'Your subscription ended. Subscribe again to resume capture.',
      actionLabel: 'Subscribe',
      action: 'checkout',
    };
  }

  // trialing
  const trialEndMs = Date.parse(s.trial_ends_at);
  if (Number.isNaN(trialEndMs)) return null;
  const msLeft = trialEndMs - now;

  if (!s.entitled) {
    // Trial expired but no card on file — trial period elapsed.
    return {
      copy: 'Your 14-day trial has ended. Subscribe to resume capture.',
      actionLabel: 'Subscribe',
      action: 'checkout',
    };
  }

  const daysLeft = Math.ceil(msLeft / MS_PER_DAY);
  if (daysLeft > 3) return null;

  const dayWord = daysLeft === 1 ? 'day' : 'days';
  const shownDays = daysLeft < 1 ? 1 : daysLeft;
  return {
    copy: `Your trial ends in ${shownDays.toString()} ${dayWord}. Subscribe to keep capture running.`,
    actionLabel: 'Subscribe',
    action: 'checkout',
  };
}

export function BillingBanner() {
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [busy, setBusy] = useState(false);

  async function load(): Promise<void> {
    if (authSession.value === null) return;
    try {
      setStatus(await getBillingStatus());
    } catch (e) {
      // Silent on 404 (endpoint not deployed yet) — banner stays hidden.
      // For real errors, do the same rather than shove a stack trace at
      // the parent from the top of every page.
      if (!(e instanceof BillingNotAvailableError)) {
        console.warn('billing status fetch failed:', e);
      }
      setStatus(null);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  // Visible-only: trial/past-due state only needs to be fresh for a
  // parent actually looking at the page.
  useVisiblePoll(() => {
    void load();
  }, REFRESH_INTERVAL_MS);

  if (status === null) return null;
  const notice = noticeFor(status, Date.now());
  if (notice === null) return null;

  async function onAction(): Promise<void> {
    if (busy || notice === null) return;
    setBusy(true);
    try {
      if (notice.action === 'checkout') {
        await startCheckout();
      } else {
        await openPortal();
      }
    } catch (e) {
      console.error('billing action failed:', e);
      setBusy(false);
    }
  }

  return (
    <div class="mx-auto max-w-4xl px-4 pt-3">
      <div
        role="status"
        class="relative flex flex-col gap-3 rounded-[4px] border border-line bg-signal-bg px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
      >
        <span
          aria-hidden
          class="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-[4px] bg-signal"
        />
        <p class="m-0 pl-2 text-[14px] leading-[1.5] text-surface-fg sm:pl-3">
          {notice.copy}
        </p>
        <div class="pl-2 sm:pl-0">
          <Button size="sm" onClick={() => void onAction()} disabled={busy}>
            {busy ? 'Loading…' : notice.actionLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
